import { SchemaFieldMeta } from './types';
import {
  FieldProp,
  FieldPropClass,
  FieldRow,
  FieldTableFieldsRow,
} from './SchemaEntityRelationsUI';
import {
  FieldClass,
  ITableField,
} from '../../../../utils/apollo/graphqlSchema';
import { useMemo } from 'react';
import KubitTooltip from '../../../../components/KubitTooltip';
import { UnreachableCaseError } from 'ts-essentials';

interface IProps {
  field: SchemaFieldMeta;
  tableNameFunc: (tableKey: string) => string;
}

const getTableFieldsStr = (
  fields: ITableField[],
  tableNameFunc: (tableKey: string) => string,
) => fields.map((f) => `${tableNameFunc(f.tableKey)}.${f.column}`).join(',');

const fieldClassColor = (cls: FieldClass, deprecated: boolean) => {
  if (deprecated) {
    return 'gray';
  }
  switch (cls) {
    case FieldClass.Basic:
      return 'LightGray';
    case FieldClass.Event:
      return 'DarkTurquoise';
    case FieldClass.Subject:
      return 'Gold';
    case FieldClass.Temporal:
      return 'LightGreen';
    default:
      throw new UnreachableCaseError(cls);
  }
};

const shortTextFromClass = (
  fieldClass: FieldClass,
  deprecated: boolean,
  filter: boolean,
  groupby: boolean,
) => {
  const deprecatedPrefix = deprecated ? 'D' : '';
  let ret = fieldClass === FieldClass.Basic ? '' : fieldClass.charAt(0);
  if (!deprecated) {
    if (filter) {
      ret += 'F';
    }
    if (groupby) {
      ret += 'B';
    }
  }
  return deprecatedPrefix + ret;
};

const longTextFromClass = (
  fieldClass: FieldClass,
  deprecated: boolean,
  filter: boolean,
  groupby: boolean,
) => {
  const deprecatedPrefix = deprecated ? '(Deprecated) ' : '';
  let fieldPx = '';
  switch (fieldClass) {
    case FieldClass.Basic:
      break;
    case FieldClass.Event:
      fieldPx = 'Event ';
      break;
    case FieldClass.Subject:
      fieldPx = 'Subject ';
      break;
    case FieldClass.Temporal:
      fieldPx = 'Temporal ';
      break;
    default:
      throw new UnreachableCaseError(fieldClass);
  }
  let ret = deprecatedPrefix + fieldPx + 'Field';
  if ((filter || groupby) && !deprecated) {
    ret += ', used as ';
    const filterBreakdown = [];
    if (filter) {
      filterBreakdown.push('Filter');
    }
    if (groupby) {
      filterBreakdown.push('Breakdown');
    }
    ret += filterBreakdown.join(' and ');
  }
  return ret;
};

const FieldRowDisplay = ({ field, tableNameFunc }: IProps) => {
  const {
    column,
    name,
    type,
    fieldClass,
    tableFields,
    deprecated,
    groupby,
    filter,
  } = field;

  const { classShortText, classLongText, classColor } = useMemo(() => {
    return {
      classShortText: shortTextFromClass(
        fieldClass,
        deprecated,
        filter,
        groupby,
      ),
      classLongText: longTextFromClass(fieldClass, deprecated, filter, groupby),
      classColor: fieldClassColor(fieldClass, deprecated),
    };
  }, [fieldClass, deprecated, filter, groupby]);

  const tableFieldsStr = getTableFieldsStr(tableFields, tableNameFunc);
  return (
    <>
      <FieldRow>
        <KubitTooltip content={column} placement="right">
          <FieldProp>{column}</FieldProp>
        </KubitTooltip>
        <KubitTooltip content={name} placement="right">
          <FieldProp>{name}</FieldProp>
        </KubitTooltip>
        <KubitTooltip content={type} placement="right">
          <FieldProp>{type}</FieldProp>
        </KubitTooltip>
        <KubitTooltip content={classLongText} placement="right">
          <FieldPropClass $color={classColor}>{classShortText}</FieldPropClass>
        </KubitTooltip>
      </FieldRow>
      {tableFields.length > 0 && (
        <FieldRow>
          <KubitTooltip content={tableFieldsStr} placement="right">
            <FieldTableFieldsRow>{tableFieldsStr}</FieldTableFieldsRow>
          </KubitTooltip>
        </FieldRow>
      )}
    </>
  );
};

export default FieldRowDisplay;
